import React, { useRef, useState } from "react";
import style from "./board-card-new.module.css";
import { useHover, useOnClickOutside } from "usehooks-ts";
import { BoardCardWrapperNew } from "./board-card-wrapper-new";
import { Board, MinimalBoard } from "shared/datamodel/schemas/board";
import { format } from "timeago.js";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { useAtomValue, useSetAtom } from "jotai";
import { userAtom } from "state-atoms/users-atoms";
import { appLoadingAtom } from "state-atoms/general-atoms";
import { InstanceType } from "shared/datamodel/schemas/ability";
import useAbility from "frontend/hooks/use-ability";
import RestrictedUserModal from "frontend/modals/restricted-user-new-modal";
import { getPathPrefix } from "../utils/getPathPrefix";
import { TrashIcon } from "frontend/icons/trash";
import { LinkIcon } from "frontend/icons/link";
import { DuplicateIcon } from "frontend/icons/duplicate";
import { MoveIcon } from "frontend/icons/move";
import { useBoardsCache } from "frontend/hooks/caching/use-boards-cache";
import { MenuHorizontalIcon } from "frontend/icons/menu-horizontal";
import ReactDOM from "react-dom";
import { PortalID } from "frontend/modal/portal-types";
import { Floater } from "frontend/ui-components/floaters/floater";
import { IllustrationIcon } from "frontend/icons/board-card/illustration";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { RenameIcon } from "frontend/icons/rename";
import RenameModal from "frontend/modals/collaboration/rename-modal";
import Modal from "frontend/modal/modal";

export function BoardCardNew({
  onClick,
  board,
  showDynamicPreview,
  onDuplicateError,
  boardIndex,
  setCanvasToMove,
}: {
  onClick?: (event: React.MouseEvent) => void;
  board: MinimalBoard;
  showDynamicPreview?: boolean;
  onDuplicateError: (maxAllowedBoards: number) => void;
  boardIndex: number;
  setCanvasToMove: (board: Board | null) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuHovered, setMenuHovered] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [boardName, setBoardName] = useState<string>(board.name);
  const { canPerformAnyActionNew } = useAbility();
  const { removeBoard, updateBoard, duplicateBoard } = useBoardsCache();

  const user = useAtomValue(userAtom);
  const setAppLoading = useSetAtom(appLoadingAtom);

  const menuButtonRef = useRef(null);
  const shouldShoWRestrictedModal = user && !canPerformAnyActionNew(user, board as Board, InstanceType.Board);
  const menuRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const cardHover = useHover(cardRef);
  const nameHover = useHover(nameRef);

  let href;
  if (process.env.NEXT_BRANCH_PATH) {
    href = `${process.env.APP_URL}${process.env.NEXT_BRANCH_PATH}/d/${board.documentId}`;
  } else {
    href = `${process.env.APP_URL}/d/${board.documentId}`;
  }

  const handleMenuMouseEnter = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuHovered(true);
  };

  const handleMenuMouseLeave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuHovered(false);
  };

  function menuClicked(e: React.MouseEvent) {
    e.stopPropagation();
    setShowMenu(!showMenu);
  }

  useOnClickOutside(menuRef, () => {
    setShowMenu(false);
  });

  const renderBoardMenu = () => {
    const element = (
      <>
        <Floater
          relativeTo={menuButtonRef}
          side={"bottom"}
          ref={menuRef}
          extraStyle={{
            zIndex: 1000,
            filter: "drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.20))",
          }}
        >
          <div className={style.canvasMenu}>
            <span data-testid="copy-link" className={style.item} onClick={copyCanvasClicked}>
              <LinkIcon />
              {"Copy link"}
            </span>
            {!board.isReadOnly && (
              <span data-testid="duplicate" className={style.item} onClick={duplicateClicked}>
                <DuplicateIcon />
                {"Duplicate canvas"}
              </span>
            )}
            {board.isOwner && (
              <>
                <span
                  data-testid="rename canvas"
                  className={style.item}
                  onClick={() => {
                    setShowMenu(false);
                    setShowRenameModal(true);
                  }}
                >
                  <RenameIcon />
                  {"Rename canvas"}
                </span>
              </>
            )}
            {board.isOwner && (
              <>
                <span data-testid="move canvas" className={style.item} onClick={moveCanvasClicked}>
                  <MoveIcon />
                  {"Move canvas"}
                </span>
              </>
            )}
            {board.isOwner && (
              <span data-testid="delete" className={style.item} onClick={deleteClicked} style={{ color: "#FF6A5F" }}>
                <TrashIcon />
                {"Delete canvas"}
              </span>
            )}
          </div>
        </Floater>
      </>
    );
    const isClient = typeof document !== "undefined";
    const portalRoot = isClient ? document.getElementById(PortalID.Floaters) : null;
    return portalRoot ? ReactDOM.createPortal(element, portalRoot) : element;
  };

  function copyCanvasClicked(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(`${location.host}/d/${board.documentId}`);
    setShowMenu(false);
  }

  function deleteClicked(e: React.MouseEvent) {
    tracking.trackAnalyticsEvent("delete_canvas", {
      category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
    });
    e.stopPropagation();
    removeBoard(board);
    setShowMenu(false);
  }

  async function duplicateClicked(e: React.MouseEvent) {
    tracking.trackAnalyticsEvent("duplicate_canvas", {
      category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
    });
    e.stopPropagation();
    setShowMenu(false);
    if (shouldShoWRestrictedModal) {
      setShowRestrictedModal(true);
      return;
    }
    if (user && user.repsToken) {
      setAppLoading(true);
      duplicateBoard({
        documentId: board.documentId,
        accountId: board.accountId,
        userId: user.id,
        userRepsToken: user.repsToken,
      })
        .catch((error) => {
          if (error?.response?.status === 406) {
            onDuplicateError(error.response.data.maxBoardsAllowed);
          }
        })
        .finally(() => setAppLoading(false));
    }
  }

  async function moveCanvasClicked(e: React.MouseEvent) {
    tracking.trackAnalyticsEvent("move canvas", {
      category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
      property: board.documentId,
    });
    e.stopPropagation();
    setShowMenu(false);
    if (shouldShoWRestrictedModal) {
      setShowRestrictedModal(true);
      return;
    }
    setCanvasToMove(board as Board);
  }

  function handleTitleChange(newName: string) {
    setBoardName(newName);
    const newBoard = { ...board, name: newName, updatedAt: new Date() };
    updateBoard(newBoard as Board);
  }

  const backgroundImage = `/images/Illustration.png`;
  const showMenuIcon = cardHover || menuHovered || showMenu;

  const renderRenameModal = () => {
    if (showRenameModal) {
      return (
        <Modal dimBackground={true}>
          <RenameModal
            currentName={boardName}
            onDismiss={() => setShowRenameModal(false)}
            type={InstanceType.Board}
            onEditName={handleTitleChange}
          />
        </Modal>
      );
    }
    return null;
  };

  return (
    <>
      <BoardCardWrapperNew>
        <a
          href={href}
          className={style.link}
          onClick={onClick}
          style={{
            cursor: menuHovered ? "default" : "pointer",
            pointerEvents: menuHovered ? "none" : "auto",
          }}
        >
          <div className={style.container} data-testid={`${board.name}-${boardIndex}`} ref={cardRef}>
            <div className={style.thumbnailContainer}>
              <div
                className={style.thumbnail}
                style={{
                  background: `url(${getPathPrefix(backgroundImage)})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: showDynamicPreview ? (board.thumbnail ? "contain" : "cover") : "100% 100%",
                  backgroundPosition: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <IllustrationIcon index={board.id % 8} />
              </div>
            </div>
            <div className={style.details} data-testid="board_card_details">
              <div className={style.boardNameSpacer}></div>
              <div className={style.subtitle}>
                <span className={style.lastModified}>Opened {format(board.lastUsed)}</span>
              </div>
            </div>
          </div>
          <div className={style.boardName} ref={nameRef}>
            {boardName}
            {nameHover && board.name.length > 20 && (
              <Tooltip
                side="right"
                label={board.name}
                relativeTo={nameRef}
                customStyle={{
                  backgroundColor: "#DEE1EB",
                  color: " #113357",
                  fontSize: 12,
                  fontWeight: 400,
                  borderRadius: 4,
                  maxWidth: "500px",
                  wordBreak: "break-word",
                }}
                withArrow={true}
              />
            )}
          </div>
        </a>
        {showMenuIcon && (
          <div
            className={style.menuIcon}
            onClick={menuClicked}
            ref={menuButtonRef}
            data-testid="board_menu"
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
            onMouseOver={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
          >
            <MenuHorizontalIcon />
          </div>
        )}
        {showRestrictedModal && (
          <RestrictedUserModal trackingEvent={"home"} onDismiss={() => setShowRestrictedModal(false)} />
        )}
      </BoardCardWrapperNew>
      {showMenu && renderBoardMenu()}
      {renderRenameModal()}
    </>
  );
}
